import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bandeau, Liste } from './Champs'
import { dateLongue } from './Messagerie'
import { ErreurApi } from '../api'
import {
  annulerVisite,
  confirmerVisite,
  constaterVisite,
  mesVisites,
  proposerVisite,
  replanifierVisite,
  type ModeVisite,
  type StatutVisite,
  type Visite,
} from '../apiRelations'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'
import { Bouton, EtatVide, Icone, Image, LienBouton, Modale, Onglets, Puce, SqueletteListe } from '../ui'

type Textes = (typeof TEXTES)['fr']
type Filtre = 'propositions' | 'avenir' | 'passees'

/** Une visite encore à trancher : proposée, ou replanifiée et donc à nouveau proposée. */
function aTrancher(visite: Visite): boolean {
  return visite.status === 'Proposed' || visite.status === 'Rescheduled'
}

function retenir(visite: Visite, filtre: Filtre): boolean {
  switch (filtre) {
    case 'propositions':
      return aTrancher(visite)
    case 'avenir':
      return visite.status === 'Confirmed'
    case 'passees':
      return visite.status === 'Completed' || visite.status === 'Cancelled' || visite.status === 'NoShow'
  }
}

/**
 * Visites — L4, revues le 13/09/2026 (maquette `docs/ui/pwa/visites.html`).
 *
 * **Confirmer un créneau lève le masquage des coordonnées du fil, et l'écran le dit AVANT.** C'est
 * le seul geste irréversible de ce lot : on ne remasque pas ce qui a été révélé. Un avertissement
 * après coup ne serait pas un avertissement — il est donc au-dessus du bouton, et nomme la
 * personne.
 *
 * **Le demandeur propose, l'hôte dispose.** C'est le titulaire de l'annonce qui reçoit, donc lui
 * qui confirme un créneau — le domaine le refuse à quiconque d'autre (<c>seeking.visit.not_host</c>).
 *
 * **Trois onglets**, repris de l'application mobile où ils ont été validés : une liste plate
 * mélangeait une visite à confirmer aujourd'hui et une visite effectuée en août. La page s'ouvre
 * sur les propositions quand il y en a, sinon sur ce qui vient.
 */
export function MesVisites({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]

  const [visites, setVisites] = useState<Visite[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)
  const [filtreChoisi, setFiltreChoisi] = useState<Filtre | null>(null)
  /** Le créneau coché par visite, côté hôte. */
  const [choix, setChoix] = useState<Record<string, string>>({})
  /** La visite dont on replanifie les créneaux, ou `null`. */
  const [replanification, setReplanification] = useState<Visite | null>(null)

  const charger = useCallback(() => {
    void mesVisites()
      .then(setVisites)
      .catch((cause: unknown) => {
        setVisites([])
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      })
  }, [langue])

  useEffect(charger, [charger])

  const agir = useCallback(
    async (action: () => Promise<void>, message: string | null = null) => {
      setErreur(null)
      try {
        await action()
        setSucces(message)
        charger()
      } catch (cause: unknown) {
        setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
      }
    },
    [charger, langue],
  )

  const toutes = visites ?? []
  const compte = (filtre: Filtre) => toutes.filter((visite) => retenir(visite, filtre)).length
  // Sans choix explicite : les propositions s'il y en a, sinon ce qui vient.
  const filtre: Filtre = filtreChoisi ?? (compte('propositions') > 0 ? 'propositions' : 'avenir')
  const liste = toutes.filter((visite) => retenir(visite, filtre))

  return (
    <section className="visites">
      <div className="page-entete">
        <h2>{t.visites}</h2>
        <p>{t.visitesIntro}</p>
        <Onglets<Filtre>
          ariaLabel={t.visitesFiltrer}
          actif={filtre}
          onChange={setFiltreChoisi}
          onglets={[
            { cle: 'propositions', libelle: avecCompte(t.visitesPropositions, compte('propositions'), 'attention') },
            { cle: 'avenir', libelle: avecCompte(t.visitesAVenir, compte('avenir')) },
            { cle: 'passees', libelle: t.visitesPassees },
          ]}
        />
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {succes !== null && <Bandeau ton="succes">{succes}</Bandeau>}
      {visites === null && <SqueletteListe />}

      {visites !== null && liste.length === 0 && erreur === null && (
        <EtatVide
          icone="calendrier"
          titre={
            filtre === 'propositions'
              ? t.visitesAucuneProposition
              : filtre === 'avenir'
                ? t.visitesAucuneAVenir
                : t.visitesAucunePassee
          }
          action={
            filtre === 'passees' ? undefined : (
              <LienBouton to="/mes-candidatures" apparence="secondaire">
                {t.mesCandidatures}
              </LienBouton>
            )
          }
        >
          {filtre === 'passees' ? undefined : t.visitesAucuneExplication}
        </EtatVide>
      )}

      <ul className="visites__liste">
        {liste.map((visite) => {
          const hote = moi !== null && visite.hostUserId === moi
          const autre = (hote ? visite.requestedByDisplayName : visite.hostDisplayName) ?? '—'
          const passee = retenir(visite, 'passees')
          const creneauChoisi = choix[visite.id] ?? visite.slots[0]?.id
          const retenu = visite.slots.find((creneau) => creneau.isSelected) ?? visite.slots[0]

          return (
            <li key={visite.id} className={passee ? 'bloc visite--passee' : 'bloc'}>
              <div className="visite__entete">
                <Image
                  mediaId={visite.listingCoverMediaId}
                  alt=""
                  ratio="4-3"
                  sizes="8rem"
                  className="visite__vignette"
                  vide={<Icone nom="maison" taille="lg" />}
                />
                <div className="visite__corps">
                  <div className="rangee rangee--espace">
                    <h3>
                      <Link to={`/annonces/${visite.listingId}`}>{visite.listingTitle ?? '—'}</Link>
                    </h3>
                    <Statut t={t} visite={visite} hote={hote} />
                  </div>
                  <p className="secondaire">
                    {aTrancher(visite)
                      ? interpoler(hote ? t.visiteProposeePar : t.visiteProposeeA, { nom: autre })
                      : interpoler(t.visiteAvec, { nom: autre })}
                    {' · '}
                    {hote ? t.visiteCoteHote : t.visiteCoteDemandeur}
                    {' · '}
                    {visite.mode === 'InPerson' ? t.surPlace : t.enVisio}
                  </p>

                  {/* Côté demandeur, en attente : les créneaux se lisent, ils ne se cochent pas. */}
                  {aTrancher(visite) && !hote && (
                    <>
                      <div className="visite__creneaux-lecture">
                        {visite.slots.map((creneau) => (
                          <span key={creneau.id} className="puce puce--action" aria-disabled="true">
                            <Icone nom="calendrier" taille="sm" />
                            <time dateTime={creneau.startsAt}>{dateLongue(langue, creneau.startsAt)}</time>
                          </span>
                        ))}
                      </div>
                      <p className="tertiaire">{interpoler(t.visiteAttenteExplication, { nom: autre })}</p>
                    </>
                  )}

                  {/* Passée : l'issue, et la date qui était retenue. */}
                  {passee && retenu !== undefined && (
                    <p className={`statut-point ${pointStatut(visite.status)}`}>
                      {libelleStatutVisite(t, visite.status)} · {dateLongue(langue, retenu.startsAt)}
                    </p>
                  )}
                </div>
              </div>

              {/* Côté hôte, à trancher : un créneau à cocher, l'avertissement, puis le geste. */}
              {aTrancher(visite) && hote && (
                <>
                  <p className="secondaire" style={{ marginBlock: 'var(--e-4) var(--e-2)' }}>
                    {t.visiteChoisirCreneau}
                  </p>
                  <div className="visite__creneaux" role="radiogroup" aria-label={t.creneaux}>
                    {visite.slots.map((creneau) => (
                      <label key={creneau.id} className="radio bloc bloc--sourd">
                        <input
                          type="radio"
                          name={`creneau-${visite.id}`}
                          value={creneau.id}
                          checked={creneauChoisi === creneau.id}
                          onChange={() => setChoix((actuels) => ({ ...actuels, [visite.id]: creneau.id }))}
                        />
                        <span>
                          <b>
                            <time dateTime={creneau.startsAt}>{dateVisite(langue, creneau.startsAt)}</time>
                          </b>
                          <small className="secondaire">
                            {visite.mode === 'InPerson' ? t.surPlace : t.enVisio} ·{' '}
                            {interpoler(t.visiteMinutes, { n: dureeMinutes(creneau.startsAt, creneau.endsAt) }, langue)}
                          </small>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ marginBlockStart: 'var(--e-4)' }}>
                    <Bandeau ton="attention" icone={<Icone nom="oeil" />}>
                      {interpoler(t.visiteAvertissementNom, { nom: autre })}
                    </Bandeau>
                  </div>
                  <div className="visite__actions">
                    <Bouton
                      desactive={creneauChoisi === undefined}
                      onClick={() => {
                        if (creneauChoisi !== undefined) {
                          void agir(() => confirmerVisite(visite.id, creneauChoisi))
                        }
                      }}
                    >
                      {t.confirmerCreneau}
                    </Bouton>
                    <Bouton apparence="secondaire" onClick={() => setReplanification(visite)}>
                      {t.visiteProposerAutres}
                    </Bouton>
                    <Bouton apparence="discret" onClick={() => void agir(() => annulerVisite(visite.id, 'other'))}>
                      {t.annulerVisite}
                    </Bouton>
                  </div>
                </>
              )}

              {aTrancher(visite) && !hote && (
                <div className="visite__actions">
                  <Bouton apparence="secondaire" taille="sm" onClick={() => setReplanification(visite)}>
                    {t.visiteModifierCreneaux}
                  </Bouton>
                  <Bouton apparence="discret" taille="sm" onClick={() => void agir(() => annulerVisite(visite.id, 'other'))}>
                    {t.annulerVisite}
                  </Bouton>
                </div>
              )}

              {/* Confirmée : la date, ce qui est révélé et où, le fil. */}
              {visite.status === 'Confirmed' && retenu !== undefined && (
                <>
                  <div className="fiche__caracteristiques" style={{ marginBlockStart: 'var(--e-4)' }}>
                    <p className="fiche__caracteristique">
                      <Icone nom="calendrier" />
                      <span>
                        <b>
                          <time dateTime={retenu.startsAt}>{dateVisite(langue, retenu.startsAt)}</time>
                        </b>
                        <span>
                          {visite.mode === 'InPerson' ? t.surPlace : t.enVisio} ·{' '}
                          {interpoler(t.visiteMinutes, { n: dureeMinutes(retenu.startsAt, retenu.endsAt) }, langue)}
                        </span>
                      </span>
                    </p>
                    <p className="fiche__caracteristique">
                      <Icone nom="carte" />
                      <span>
                        <b>{t.visiteAdresseDansLeFil}</b>
                        <span>{t.visiteRevelesConfirmation}</span>
                      </span>
                    </p>
                    <p className="fiche__caracteristique">
                      <Icone nom="message" />
                      <span>
                        <b>
                          <Link to="/messages">{t.visiteOuvrirFil}</Link>
                        </b>
                        <span>{t.visiteOuvrirFilAide}</span>
                      </span>
                    </p>
                  </div>
                  <div className="visite__actions">
                    {/* Constater l'issue est réservé à l'hôte : c'est lui qui a vu qui est venu. */}
                    {hote && (
                      <>
                        <Bouton apparence="secondaire" onClick={() => void agir(() => constaterVisite(visite.id, 'Completed'))}>
                          {t.visiteEffectuee}
                        </Bouton>
                        <Bouton apparence="discret" onClick={() => void agir(() => constaterVisite(visite.id, 'NoShow'))}>
                          {t.visiteAbsenceVisiteur}
                        </Bouton>
                      </>
                    )}
                    <Bouton apparence="discret" onClick={() => void agir(() => annulerVisite(visite.id, 'other'))}>
                      {t.annulerVisite}
                    </Bouton>
                  </div>
                  {hote && <p className="tertiaire">{t.visiteConstatReserve}</p>}
                </>
              )}
            </li>
          )
        })}
      </ul>

      {replanification !== null && (
        <Replanifier
          langue={langue}
          visite={replanification}
          onFermer={() => setReplanification(null)}
          onEnvoye={() => {
            setReplanification(null)
            setSucces(t.visiteReplanifiee)
            charger()
          }}
        />
      )}
    </section>
  )
}

/** Le libellé d'un onglet et son compte, quand il y en a un : « Propositions · 2 ». */
function avecCompte(libelle: string, compte: number, ton?: 'attention') {
  if (compte === 0) {
    return libelle
  }

  return (
    <>
      {libelle} <Puce ton={ton}>{compte}</Puce>
    </>
  )
}

function Statut({ t, visite, hote }: { t: Textes; visite: Visite; hote: boolean }) {
  if (aTrancher(visite)) {
    return hote ? <Puce ton="attention">{t.visiteCreneauxProposes}</Puce> : <Puce ton="info">{t.visiteEnAttenteHote}</Puce>
  }

  switch (visite.status) {
    case 'Confirmed':
      return <Puce ton="succes">{t.visiteStatutConfirmed}</Puce>
    case 'NoShow':
      return <Puce ton="danger">{t.visiteStatutNoShow}</Puce>
    default:
      return <Puce>{libelleStatutVisite(t, visite.status)}</Puce>
  }
}

function pointStatut(statut: StatutVisite): string {
  switch (statut) {
    case 'Completed':
      return 'statut-point--succes'
    case 'NoShow':
      return 'statut-point--danger'
    default:
      return ''
  }
}

/**
 * La date d'un créneau, en toutes lettres — « mercredi 16 septembre, 14:05 ». Le jour de la semaine
 * est ce qu'on regarde d'abord pour un rendez-vous ; `dateLongue`, plus courte, reste pour les
 * listes serrées.
 */
function dateVisite(langue: Langue, iso: string): string {
  return new Intl.DateTimeFormat(langue, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function dureeMinutes(debut: string, fin: string): number {
  return Math.max(0, Math.round((new Date(fin).getTime() - new Date(debut).getTime()) / 60000))
}

/**
 * Nouveaux créneaux pour une visite existante, dans une feuille.
 *
 * Six créneaux au maximum — la borne vient du domaine. Chaque créneau dure trente minutes, comme
 * à la proposition initiale : la durée n'est pas une donnée qu'on demande, c'est une convention.
 */
function Replanifier({
  langue,
  visite,
  onFermer,
  onEnvoye,
}: {
  langue: Langue
  visite: Visite
  onFermer: () => void
  onEnvoye: () => void
}) {
  const t = TEXTES[langue]
  const [creneaux, setCreneaux] = useState<string[]>([''])
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const retenus = creneaux.filter((valeur) => valeur !== '')

  async function envoyer() {
    if (retenus.length === 0 || envoi) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    try {
      await replanifierVisite(
        visite.id,
        retenus.map((valeur) => {
          const debut = new Date(valeur)
          return { startsAt: debut.toISOString(), endsAt: new Date(debut.getTime() + 30 * 60 * 1000).toISOString() }
        }),
      )
      onEnvoye()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Modale
      titre={t.visiteProposerAutres}
      feuille
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton charge={envoi} desactive={retenus.length === 0} onClick={() => void envoyer()}>
            {t.visiteEnvoyerCreneaux}
          </Bouton>
        </>
      }
    >
      <div className="visite__creneaux-saisie">
        <p className="secondaire">{visite.listingTitle ?? '—'}</p>
        {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
        {creneaux.map((valeur, index) => (
          <p className="champ" key={index} style={{ margin: 0 }}>
            <label htmlFor={`replanif-${index}`}>{`${t.creneaux} ${index + 1}`}</label>
            <input
              id={`replanif-${index}`}
              type="datetime-local"
              value={valeur}
              onChange={(evenement) =>
                setCreneaux((precedents) =>
                  precedents.map((autre, rang) => (rang === index ? evenement.target.value : autre)),
                )
              }
            />
          </p>
        ))}
        {creneaux.length < 6 && (
          <div>
            <Bouton apparence="secondaire" taille="sm" onClick={() => setCreneaux((p) => [...p, ''])}>
              <Icone nom="plus" taille="sm" /> {t.ajouterCreneau}
            </Bouton>
          </div>
        )}
        <p className="aide">{t.visiteCreneauxAide}</p>
      </div>
    </Modale>
  )
}

/**
 * Proposition de créneaux, côté demandeur — depuis une candidature ou la fiche.
 *
 * Six créneaux au maximum — la borne vient du domaine. En proposer trente reviendrait à ne rien
 * proposer : personne ne lit une liste de trente heures.
 */
export function ProposerVisite({
  langue,
  annonceId,
  candidatureId,
  onPropose,
}: {
  langue: Langue
  annonceId: string
  candidatureId: string | null
  onPropose?: () => void
}) {
  const t = TEXTES[langue]

  const [mode, setMode] = useState<ModeVisite>('InPerson')
  const [creneaux, setCreneaux] = useState<string[]>([''])
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState(false)

  const soumettre = useCallback(async () => {
    const retenus = creneaux
      .filter((valeur) => valeur !== '')
      .map((valeur) => {
        const debut = new Date(valeur)
        const fin = new Date(debut.getTime() + 30 * 60 * 1000)

        return { startsAt: debut.toISOString(), endsAt: fin.toISOString() }
      })

    if (retenus.length === 0 || envoi) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    try {
      await proposerVisite(annonceId, candidatureId, mode, retenus)
      setSucces(true)
      setCreneaux([''])
      onPropose?.()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }, [annonceId, candidatureId, creneaux, envoi, langue, mode, onPropose])

  return (
    <form
      className="proposer-visite"
      onSubmit={(evenement) => {
        evenement.preventDefault()
        void soumettre()
      }}
    >
      <h3>{t.proposerVisite}</h3>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {succes && <Bandeau ton="succes">{t.proposerVisite}</Bandeau>}

      <Liste
        id="mode-visite"
        libelle={t.proposerVisite}
        valeur={mode}
        options={[
          { valeur: 'InPerson', libelle: t.surPlace },
          { valeur: 'Video', libelle: t.enVisio },
        ]}
        onChange={(valeur) => setMode(valeur as ModeVisite)}
      />

      <fieldset className="creneaux-saisie">
        <legend>{t.creneaux}</legend>

        {creneaux.map((valeur, index) => (
          <p className="champ" key={index}>
            <label htmlFor={`creneau-${index}`}>{`${index + 1}`}</label>
            <input
              id={`creneau-${index}`}
              type="datetime-local"
              value={valeur}
              onChange={(evenement) =>
                setCreneaux((precedents) =>
                  precedents.map((autre, rang) => (rang === index ? evenement.target.value : autre)),
                )
              }
            />
          </p>
        ))}

        {creneaux.length < 6 && (
          <button type="button" className="lien" onClick={() => setCreneaux((p) => [...p, ''])}>
            {t.ajouterCreneau}
          </button>
        )}
      </fieldset>

      <button type="submit" className="bouton-principal" disabled={envoi}>
        {envoi ? t.chargement : t.proposerVisite}
      </button>
    </form>
  )
}

function libelleStatutVisite(t: Textes, statut: StatutVisite): string {
  switch (statut) {
    case 'Proposed':
      return t.visiteStatutProposed
    case 'Confirmed':
      return t.visiteStatutConfirmed
    case 'Rescheduled':
      return t.visiteStatutRescheduled
    case 'Completed':
      return t.visiteStatutCompleted
    case 'Cancelled':
      return t.visiteStatutCancelled
    case 'NoShow':
      return t.visiteStatutNoShow
  }
}
